import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Calendar, DollarSign, Tag, AlertTriangle, Check, TrendingUp, TrendingDown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('pt-BR');
};

const CATEGORIAS = [
  { value: 'antecipado', label: 'Antecipado', desc: 'Passagens, Hotel, Seguro', color: 'blue' },
  { value: 'recorrente', label: 'Recorrente', desc: 'Alimentação, Transporte', color: 'green' },
  { value: 'pontual', label: 'Pontual', desc: 'Emergências, Compras', color: 'orange' }
];

function ModalGastosViagem({ viagem, estatisticas, onClose, getAuthHeaders, onUpdate }) {
  const [activeTab, setActiveTab] = useState('resumo');
  const [gastos, setGastos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  
  const [novoGasto, setNovoGasto] = useState({
    descricao: '',
    valor: '',
    data_pagamento: new Date().toISOString().split('T')[0],
    categoria: 'recorrente'
  });

  useEffect(() => {
    if (viagem) {
      loadGastos();
    }
  }, [viagem]);

  const loadGastos = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/viagens/${viagem.viagem_id}/gastos`, getAuthHeaders());
      setGastos(response.data.gastos || []);
    } catch (error) {
      console.error('Erro ao carregar gastos:', error);
      toast.error('Erro ao carregar gastos');
    } finally {
      setLoading(false);
    }
  };

  const handleAddGasto = async (e) => {
    e.preventDefault();
    
    if (!novoGasto.descricao || !novoGasto.valor || !novoGasto.data_pagamento) {
      toast.error('Preencha todos os campos');
      return;
    }

    try {
      await axios.post(`${API}/viagens/${viagem.viagem_id}/gastos`, {
        ...novoGasto,
        viagem_id: viagem.viagem_id,
        valor: parseFloat(novoGasto.valor)
      }, getAuthHeaders());
      
      toast.success('Gasto adicionado!');
      setNovoGasto({
        descricao: '',
        valor: '',
        data_pagamento: new Date().toISOString().split('T')[0],
        categoria: 'recorrente'
      });
      setShowAddForm(false);
      loadGastos();
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar gasto');
    }
  };

  const handleDeleteGasto = async (gastoId) => {
    if (!window.confirm('Excluir este gasto?')) return;
    
    try {
      await axios.delete(`${API}/viagens/gastos/${gastoId}`, getAuthHeaders());
      toast.success('Gasto excluído');
      loadGastos();
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir gasto');
    }
  };

  const getCategoriaInfo = (cat) => {
    return CATEGORIAS.find(c => c.value === cat) || CATEGORIAS[1];
  };

  const getIndicadorColor = (indicador) => {
    switch(indicador) {
      case 'verde': return 'bg-green-500';
      case 'amarelo': return 'bg-yellow-500';
      case 'vermelho': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'Planejada': return 'bg-gray-100 text-gray-700';
      case 'Em andamento': return 'bg-blue-100 text-blue-700';
      case 'Concluída': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Calcular valores DINAMICAMENTE com base nos gastos carregados
  const totalGastosCalculado = gastos.reduce((sum, g) => sum + (g.valor || 0), 0);
  const totalAntecipados = gastos.filter(g => g.categoria === 'antecipado').reduce((sum, g) => sum + (g.valor || 0), 0);
  const totalRecorrentes = gastos.filter(g => g.categoria === 'recorrente').reduce((sum, g) => sum + (g.valor || 0), 0);
  const totalPontuais = gastos.filter(g => g.categoria === 'pontual').reduce((sum, g) => sum + (g.valor || 0), 0);
  
  // Usar orçamento atual da viagem/estatísticas
  const orcamentoAtual = estatisticas?.orcamento || viagem?.orcamento || 0;
  
  // SALDO CALCULADO DINAMICAMENTE - sempre reflete o estado atual
  const saldoCalculado = orcamentoAtual - totalGastosCalculado;
  
  // INDICADOR CALCULADO DINAMICAMENTE
  const indicadorCalculado = (() => {
    if (orcamentoAtual <= 0) return 'verde'; // Sem orçamento definido, não mostrar erro
    if (totalGastosCalculado > orcamentoAtual) return 'vermelho';
    if (totalGastosCalculado > orcamentoAtual * 0.9) return 'amarelo';
    return 'verde';
  })();

  const isReadOnly = estatisticas?.status === 'Concluída';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-6 text-white">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-2xl font-bold">{viagem?.nome_viagem}</h2>
              <div className="flex items-center gap-2 mt-2">
                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${getStatusBadge(estatisticas?.status)}`}>
                  {estatisticas?.status || 'Planejada'}
                </span>
                {estatisticas?.dias_totais > 0 && (
                  <span className="text-white/80 text-sm">
                    {estatisticas.dias_passados}/{estatisticas.dias_totais} dias
                  </span>
                )}
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {/* Indicador de saúde - CALCULADO DINAMICAMENTE */}
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${getIndicadorColor(indicadorCalculado)}`}></div>
            <span className="text-sm text-white/90">
              {indicadorCalculado === 'verde' && 'Dentro do planejado'}
              {indicadorCalculado === 'amarelo' && 'Atenção - acima do esperado'}
              {indicadorCalculado === 'vermelho' && 'Crítico - orçamento estourado'}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          {['resumo', 'gastos', 'historico'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === tab 
                  ? 'text-purple-600 border-b-2 border-purple-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'resumo' && 'Resumo'}
              {tab === 'gastos' && 'Adicionar'}
              {tab === 'historico' && 'Histórico'}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* TAB RESUMO */}
          {activeTab === 'resumo' && (
            <div className="space-y-6">
              {/* Cards de resumo */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 mb-1">Orçamento</p>
                  <p className="text-xl font-bold text-gray-900">{formatCurrency(orcamentoAtual)}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 mb-1">Saldo Restante</p>
                  <p className={`text-xl font-bold ${saldoCalculado >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(saldoCalculado)}
                  </p>
                </div>
              </div>

              {/* Gastos - CALCULADOS DINAMICAMENTE */}
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-4">Gastos</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">Antecipados</span>
                    </div>
                    <span className="font-semibold">{formatCurrency(totalAntecipados)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">Recorrentes</span>
                    </div>
                    <span className="font-semibold">{formatCurrency(totalRecorrentes)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">Pontuais</span>
                    </div>
                    <span className="font-semibold">{formatCurrency(totalPontuais)}</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between items-center">
                    <span className="font-medium text-gray-900">Total Gasto</span>
                    <span className="font-bold text-lg">{formatCurrency(totalGastosCalculado)}</span>
                  </div>
                </div>
              </div>

              {/* Projeção */}
              {estatisticas?.dias_totais > 0 && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                  <h3 className="font-semibold text-purple-900 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Projeção
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-purple-600">Custo Diário Diluído</p>
                      <p className="font-bold text-purple-900">{formatCurrency(estatisticas?.custo_diluido_dia)}</p>
                    </div>
                    <div>
                      <p className="text-purple-600">Custo Variável/Dia</p>
                      <p className="font-bold text-purple-900">{formatCurrency(estatisticas?.custo_variavel_dia)}</p>
                    </div>
                    <div>
                      <p className="text-purple-600">Custo Diário Projetado</p>
                      <p className="font-bold text-purple-900">{formatCurrency(estatisticas?.custo_diario_projetado)}</p>
                    </div>
                    <div>
                      <p className="text-purple-600">Gasto Acumulado Projetado</p>
                      <p className="font-bold text-purple-900">{formatCurrency(estatisticas?.gasto_acumulado_projetado)}</p>
                    </div>
                  </div>
                  
                  {/* Comparação */}
                  <div className="mt-4 pt-4 border-t border-purple-200">
                    <div className="flex justify-between items-center">
                      <span className="text-purple-700">Real vs Projetado</span>
                      <div className="flex items-center gap-2">
                        {(estatisticas?.total_gastos || 0) > (estatisticas?.gasto_acumulado_projetado || 0) ? (
                          <>
                            <TrendingUp className="w-4 h-4 text-red-500" />
                            <span className="text-red-600 font-semibold">
                              +{formatCurrency((estatisticas?.total_gastos || 0) - (estatisticas?.gasto_acumulado_projetado || 0))}
                            </span>
                          </>
                        ) : (
                          <>
                            <TrendingDown className="w-4 h-4 text-green-500" />
                            <span className="text-green-600 font-semibold">
                              {formatCurrency((estatisticas?.gasto_acumulado_projetado || 0) - totalGastosCalculado)} economia
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Alerta de estouro - CALCULADO DINAMICAMENTE */}
              {saldoCalculado < 0 && orcamentoAtual > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-red-800">Orçamento Estourado</h4>
                      <p className="text-sm text-red-600 mt-1">
                        Você excedeu o orçamento em {formatCurrency(Math.abs(saldoCalculado))}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB ADICIONAR GASTO */}
          {activeTab === 'gastos' && (
            <div>
              {isReadOnly ? (
                <div className="text-center py-8">
                  <Check className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <p className="text-gray-600">Esta viagem está concluída.</p>
                  <p className="text-sm text-gray-500">Não é possível adicionar novos gastos.</p>
                </div>
              ) : (
                <form onSubmit={handleAddGasto} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                    <input
                      type="text"
                      value={novoGasto.descricao}
                      onChange={(e) => setNovoGasto({...novoGasto, descricao: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Ex: Hotel, Passagem, Almoço..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Valor</label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={novoGasto.valor}
                        onChange={(e) => setNovoGasto({...novoGasto, valor: e.target.value})}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="0,00"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data do Pagamento</label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="date"
                        value={novoGasto.data_pagamento}
                        onChange={(e) => setNovoGasto({...novoGasto, data_pagamento: e.target.value})}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Categoria</label>
                    <div className="grid grid-cols-1 gap-2">
                      {CATEGORIAS.map((cat) => (
                        <button
                          key={cat.value}
                          type="button"
                          onClick={() => setNovoGasto({...novoGasto, categoria: cat.value})}
                          className={`p-3 rounded-xl border-2 text-left transition-all ${
                            novoGasto.categoria === cat.value
                              ? 'border-purple-500 bg-purple-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full bg-${cat.color}-500`}></div>
                            <div>
                              <p className="font-medium text-gray-900">{cat.label}</p>
                              <p className="text-xs text-gray-500">{cat.desc}</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-purple-600 text-white py-3 rounded-xl font-semibold hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    Adicionar Gasto
                  </button>
                </form>
              )}
            </div>
          )}

          {/* TAB HISTÓRICO */}
          {activeTab === 'historico' && (
            <div>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                </div>
              ) : gastos.length === 0 ? (
                <div className="text-center py-8">
                  <Tag className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Nenhum gasto registrado</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {gastos.map((gasto) => {
                    const catInfo = getCategoriaInfo(gasto.categoria);
                    return (
                      <div key={gasto.id} className="bg-gray-50 rounded-xl p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <div className={`w-2 h-2 rounded-full bg-${catInfo.color}-500`}></div>
                              <span className="font-medium text-gray-900">{gasto.descricao}</span>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-gray-500">
                              <span>{formatDate(gasto.data_pagamento)}</span>
                              <span className={`px-2 py-0.5 rounded text-xs bg-${catInfo.color}-100 text-${catInfo.color}-700`}>
                                {catInfo.label}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-gray-900">{formatCurrency(gasto.valor)}</span>
                            {!isReadOnly && (
                              <button
                                onClick={() => handleDeleteGasto(gasto.id)}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ModalGastosViagem;
